/**
 * The one row above the picture on the check screen (#648): "Page 2 · Question 1" in the course ink, and at its end a dot
 * for each line that still needs the learner, the current one filled. The dots are position, never progress: they say
 * where in a short walk the learner is, and nothing about how much of anything is right or known. The question name is
 * the line's label, untrusted text from the learner's page, so it goes through `QuestionWords`.
 */

import { Link } from "react-router";
import { useHomeworkLabels } from "../../i18n/homework-labels.js";
import { QuestionWords } from "./question-label.js";

export interface CheckStep {
  /** 0-based position of the line on screen among the lines that need the learner. */
  readonly index: number;
  readonly count: number;
  /** How many lines still need the learner after this one: said in words ("2 lines left", "Last line"). */
  readonly after: number;
  /** The question the current line is part of; null when none is in force. */
  readonly label: string | null;
}

export function CheckRow({
  page,
  step,
  needing,
  pageTo,
}: {
  readonly page: string;
  /** Where "Page 2" leads (the summary). Absent: it is plain text. */
  readonly pageTo?: string | undefined;
  readonly step?: CheckStep | undefined;
  /** With no step on screen (the whole list): how many lines still need the learner, in words. */
  readonly needing?: number | undefined;
}): React.JSX.Element {
  const t = useHomeworkLabels();
  return (
    <nav className="check-row" aria-label={t.pagesHeading}>
      <div className="check-row-left">
        <p className="check-row-where">
          {pageTo === undefined ? (
            page
          ) : (
            <Link className="check-row-page" to={pageTo}>
              {page}
              <span className="visually-hidden"> · {t.backToSummary}</span>
            </Link>
          )}
          {step !== undefined && step.label !== null ? (
            <>
              {" · "}
              <QuestionWords label={step.label} />
            </>
          ) : null}
        </p>
        {step !== undefined || needing !== undefined ? (
          <p className="check-row-count" role="status">
            {step !== undefined
              ? t.linesLeft(step.after)
              : t.stillNeed(needing ?? 0)}
          </p>
        ) : null}
      </div>
      {step !== undefined && step.count > 1 ? (
        <span
          className="check-dots"
          role="img"
          aria-label={t.stepAria(step.index + 1)}
        >
          {Array.from({ length: step.count }, (_, i) => (
            <span
              key={i}
              className="check-dot"
              data-current={i === step.index ? "true" : undefined}
            />
          ))}
        </span>
      ) : null}
    </nav>
  );
}
